"use client";

import Link from "next/link";
import { Button } from "../ui/button";
import { TamiBrandLog } from "../ui/icons/personal-brand/tami-brand-log";
import { useEffect, useRef, useState } from "react";
import { Menu, X } from "lucide-react";
import { TamiButton } from "@/components/tami-button";
import { cn } from "@/lib/utils";
import { HEADER_PUBLIC_COPY } from "./header.copy";
import { LanguageMenu, SegmentedControl } from "@tami/ui/web";
import { LANGUAGE_SETTINGS_COPY } from "@tami/core";
import { useSiteLocale } from "@/hooks/use-site-locale";
import { getLocale, LOCALE_NAMES, SUPPORTED_LOCALES } from "@tami/core/intl";
import { localizedPath } from "@/lib/i18n/locale-path";

const PUBLIC_NAV_LINKS = [
  { hash: "hero-demo", labelKey: "offer", testId: "start-link" },
  { hash: "depoimentos", labelKey: "testimonials", testId: "testimonials-link" },
  { hash: "pricing", labelKey: "pricing", testId: "features-link" },
] as const;

/** A home do idioma da página (`/`, `/en`, `/es`), com a âncora. */
const homeHref = (hash: string): string => `${localizedPath("/", getLocale())}#${hash}`;

const scrollToHash = (hash: string) => {
  const target = document.getElementById(hash);
  if (!target) return false;
  target.scrollIntoView({ behavior: "smooth", block: "start" });
  window.history.pushState(null, "", `#${hash}`);
  return true;
};

const handleHashNavClick = (
  event: React.MouseEvent<HTMLAnchorElement>,
  hash: string,
) => {
  if (window.location.pathname !== localizedPath("/", getLocale())) return;
  if (scrollToHash(hash)) {
    event.preventDefault();
  }
};

export function HeaderPublic() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const firstSectionHeightRef = useRef(0);
  const siteLocale = useSiteLocale();

  useEffect(() => {
    const updateFirstSectionHeight = () => {
      const firstSection =
        document.querySelector("section:first-of-type") ||
        document.querySelector('[data-section="first"]') ||
        document.querySelector(".hero-section");
      if (firstSection) {
        firstSectionHeightRef.current = (
          firstSection as HTMLElement
        ).offsetHeight;
      }
    };

    const handleScroll = () => {
      setIsScrolled(window.scrollY > firstSectionHeightRef.current / 2);
    };

    updateFirstSectionHeight();
    window.addEventListener("resize", updateFirstSectionHeight);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateFirstSectionHeight);
    };
  }, []);

  const handleClose = () => setIsMobileMenuOpen(false);

  return (
    <>
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-[100] flex h-16 w-full items-center justify-between gap-3 px-4 text-gradient-foreground transition-all duration-300 md:px-6 lg:px-8",
          isScrolled
            ? "shadow-lg bg-tami-default/95 backdrop-blur-md"
            : "bg-transparent",
        )}
      >
        <Link
          href={homeHref("start")}
          className="flex shrink-0 items-center gap-2"
          prefetch={false}
          aria-label={HEADER_PUBLIC_COPY.homeAria}
        >
          <TamiBrandLog className="h-auto w-20 text-gradient-foreground sm:w-24 md:w-28" />
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-6 text-sm font-medium lg:flex">
          {PUBLIC_NAV_LINKS.map((link) => (
            <Button
              key={link.hash}
              variant="link"
              asChild
              className="home-public-nav-link !text-gradient-foreground"
            >
              <Link
                href={homeHref(link.hash)}
                prefetch={false}
                data-testid={link.testId}
                onClick={(event) => handleHashNavClick(event, link.hash)}
              >
                {HEADER_PUBLIC_COPY.nav[link.labelKey]}
              </Link>
            </Button>
          ))}
        </nav>

        <div className="hidden items-center justify-end gap-4 lg:flex">
          <LanguageMenu
            value={siteLocale.locale}
            onValueChange={(next) => void siteLocale.change(next)}
            className="text-gradient-foreground"
            contentClassName="z-[110]"
          />
          <TamiButton variant="black" href="/sign-in" data-testid="sign-link">
            {HEADER_PUBLIC_COPY.signInDesktop}
          </TamiButton>
          <TamiButton variant="primary" href="/sign-up" data-testid="sign-up-link">
            {HEADER_PUBLIC_COPY.createAccount}
          </TamiButton>
        </div>

        <button
          type="button"
          onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-md touch-manipulation text-tami-dark transition-opacity hover:opacity-70 lg:hidden"
          data-testid="public-menu-toggle"
          aria-label={
            isMobileMenuOpen
              ? HEADER_PUBLIC_COPY.menuClose
              : HEADER_PUBLIC_COPY.menuOpen
          }
          aria-expanded={isMobileMenuOpen}
          aria-controls="public-mobile-nav"
        >
          {isMobileMenuOpen ? (
            <X className="size-5" aria-hidden />
          ) : (
            <Menu className="size-5" aria-hidden />
          )}
        </button>
      </header>

      {/* Mobile menu — sem portal, sem overlay do Radix */}
      <nav
        id="public-mobile-nav"
        aria-label={HEADER_PUBLIC_COPY.mobileNavAria}
        className={cn(
          "fixed inset-x-0 top-16 z-[99] border-b border-black/10 bg-tami-default px-4 pb-6 pt-4 shadow-xl transition-all duration-200 lg:hidden",
          isMobileMenuOpen
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-2 pointer-events-none",
        )}
      >
        <div className="flex flex-col gap-1">
          {PUBLIC_NAV_LINKS.map((link) => (
            <Link
              key={link.hash}
              href={homeHref(link.hash)}
              prefetch={false}
              data-testid={link.testId}
              onClick={(event) => {
                handleHashNavClick(event, link.hash);
                handleClose();
              }}
              className="block py-3 text-base font-medium text-tami-dark transition-opacity hover:opacity-70 active:opacity-50"
              tabIndex={isMobileMenuOpen ? 0 : -1}
            >
              {HEADER_PUBLIC_COPY.nav[link.labelKey]}
            </Link>
          ))}

          <div className="mt-2 border-t border-black/10 pt-4">
            <SegmentedControl
              options={SUPPORTED_LOCALES.map((locale) => ({
                value: locale,
                label: LOCALE_NAMES[locale],
                testId: `language-option-${locale}`,
              }))}
              value={siteLocale.locale}
              onValueChange={(next) => void siteLocale.change(next)}
              label={LANGUAGE_SETTINGS_COPY.groupLabel}
              className="border-black/10 text-tami-dark"
            />
          </div>

          <div className="mt-2 flex flex-col gap-2 border-t border-black/10 pt-4">
            <Link
              href="/sign-in"
              prefetch={false}
              data-testid="sign-link"
              onClick={handleClose}
              className="block py-3 text-base font-medium text-tami-dark transition-opacity hover:opacity-70 active:opacity-50"
              tabIndex={isMobileMenuOpen ? 0 : -1}
            >
              {HEADER_PUBLIC_COPY.signInMobile}
            </Link>

            <TamiButton
              variant="primary"
              href="/sign-up"
              className="w-full"
              data-testid="sign-up-link"
              onClick={handleClose}
            >
              {HEADER_PUBLIC_COPY.createAccount}
            </TamiButton>
          </div>
        </div>
      </nav>

      {/* Backdrop para fechar o menu ao tocar fora */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-[98] lg:hidden"
          aria-hidden
          onClick={handleClose}
        />
      )}
    </>
  );
}
